import ICON_CUSTOM from '@/assets/airport/icon_custom.png';
import ICON_GUIDE from '@/assets/airport/icon_guide.png';
import ICON_PRINT from '@/assets/airport/icon_print.png';
import ICON_RECEIPT from '@/assets/airport/icon_receipt.png';
import ICON_REMITTANCE from '@/assets/airport/icon_remittance.png';
import DetailBillModal from '@/components/Airport/DetailBillModal';
import ReceiptLayout from '@/components/Receipt/ReceiptLayout';
import { routes } from '@/constants/routes.const';
import useGetAirportList from '@/hooks/useGetAirportList';
import { useAppDispatch, useAppSelector } from '@/hooks/useReduxHooks';
import { IGlobalState, initialGlobalState } from '@/store';
import {
  LoadingView,
  DepartureWarning,
  RefundAmount,
  RefundBillList,
  TextButton,
} from '@repo/components';
import { AIRPORT_LIST, SELECT_LIST } from '@repo/constants';
import { Button, Icon } from '@successmode-ktp/kui';
import { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';

const Airport = () => {
  const history = useHistory();
  const dispatch = useAppDispatch();
  const { passportNumber, nationality, isUploadRequired } = useAppSelector(
    (state) => state,
  ) as IGlobalState;
  const selectedAirport =
    passportNumber && localStorage.getItem(passportNumber);
  const isDeparture = localStorage.getItem('departureCheck') === 'yes';
  const [openedBillIndex, setOpenedBillIndex] = useState(-1);

  useEffect(() => {
    localStorage.setItem(`${passportNumber}-transfer`, 'false');
  }, []);

  const { data } = useGetAirportList(passportNumber);

  const onOpenLogoutModal = () => {
    dispatch({
      type: 'UPDATE_HOME_MODAL_STATE',
      payload: {
        isActive: true,
        type: 'CUSTOM',
        data: {
          title: nationality === 'KOR' ? '로그아웃 할까요?' : 'Sign Out',
          content:
            nationality === 'KOR'
              ? '다른 여권번호로 조회하려면 로그아웃 하세요.'
              : `Sign out to look up\nanother passport number.`,
          btnText: nationality === 'KOR' ? '로그아웃' : 'Sign Out',
          subBtnText: nationality === 'KOR' ? '취소' : 'Cancel',
          btnCallback: logout,
        },
      },
    });
  };

  const logout = () => {
    dispatch({
      type: 'UPDATE_USER_INFO',
      payload: initialGlobalState,
    });
    // isReadAirportGuide를 제외하고 모든 localStorage 항목 삭제
    const isReadAirportGuide = localStorage.getItem('isReadAirportGuide');
    localStorage.clear();
    if (isReadAirportGuide) {
      localStorage.setItem('isReadAirportGuide', isReadAirportGuide);
    }
    history.replace(routes.passport);
  };

  const nationIndex = nationality === 'KOR' ? 1 : 0;
  const text = {
    contact: ['Contact', '문의하기'],
    myRefund: ['My Refund', '내 환급금'],
    refundGuide: ['How to get a refund?', '환급절차 안내'],
    myReceipts: ['My Receipts', '나의 택스리펀 영수증'],
    printReceipts: ['Print my Refund Receipts', '환급전표 출력'],
    printReceiptShort: ['Print my Receipts', '환급전표 출력'],
    kioskLocation: ['Customs Location', '세관 위치'],
    getByRemittance: ['Get refund by Remittance', '송금으로 환급받기'],
    logOut: ['Log out', '로그아웃'],
  };

  // 한국인은 반출승인번호 유무와 관계없이 반출승인번호가 있는 경우의 플로우로 진행
  const hasTakeOutNumber = nationality === 'KOR' || !!data?.hasTakeOutNumber;

  if (!data) {
    return <LoadingView />;
  }

  return (
    <>
      <ReceiptLayout>
        <ButtonWrapper>
          {/* 해당 출국 여부 잘못 선택했을 때 되돌아 갈 수 있도록 버튼 추가됨 */}
          <Button
            onClick={() => history.push(routes.selectAirport)}
            size='sm'
            variant='line'
            style={{
              boxShadow: '0px 0px 4px 0px #6A9DFF',
              gap: '6px',
              padding: '0 12px',
              width: 'auto',
            }}
          >
            <Icon.Departure size={20} fillColor='#246CF6' />
            {selectedAirport
              ? SELECT_LIST.find(
                  (v) => v.value === decodeURIComponent(selectedAirport),
                )?.[nationality === 'KOR' ? 'korLabel' : 'label']
              : 'Airport'}
          </Button>
        </ButtonWrapper>
        {!hasTakeOutNumber && !isDeparture && (
          <Title>Print & Submit receipt to customs.</Title>
        )}
        <RefundAmount
          refundAmount={data?.total}
          nation={nationality}
          onClick={() =>
            history.push({
              pathname: routes.receipt,
              state: { refundAmount: data?.total || 0 },
            })
          }
        />
        {localStorage.getItem('departureCheck') === 'yes' && (
          <DepartureWarning nationality={nationality} />
        )}
        <RefundSection>
          {data?.items && (
            <RefundBillList
              type='store'
              billList={data.items}
              onClick={setOpenedBillIndex}
              nation={nationality}
            />
          )}
        </RefundSection>
        {!hasTakeOutNumber && !isDeparture && (
          <Button
            onClick={() =>
              history.push({
                pathname: routes.airportPrintReceipt,
                state: { displays: data?.items || [] },
              })
            }
            size='lg'
            style={{
              width: '100%',
              fontSize: '14px',
              fontWeight: 600,
              letterSpacing: '-0.28px',
            }}
          >
            {text.printReceipts[nationIndex]}
          </Button>
        )}
        <DescriptionWrapper>
          <DescriptionButton onClick={() => history.push(routes.airportGuide)}>
            <Description>
              <img src={ICON_GUIDE} alt='' width={24} />
              {text.refundGuide[nationIndex]}
            </Description>
            <Icon.ChevronRight size={24} fillColor='#111111' />
          </DescriptionButton>
          {hasTakeOutNumber && (
            <DescriptionButton
              onClick={() =>
                history.push({
                  pathname: routes.receipt,
                  state: { refundAmount: data?.total || 0 },
                })
              }
            >
              <Description>
                <img src={ICON_RECEIPT} alt='' width={24} />
                {text.myReceipts[nationIndex]}
              </Description>
              <Icon.ChevronRight size={24} fillColor='#111111' />
            </DescriptionButton>
          )}
          {!hasTakeOutNumber && isDeparture && (
            <DescriptionButton
              onClick={() =>
                history.push({
                  pathname: routes.airportPrintReceipt,
                  state: { displays: data?.items || [] },
                })
              }
            >
              <Description>
                <img src={ICON_PRINT} alt='' width={24} />
                {text.printReceiptShort[nationIndex]}
              </Description>
              <Icon.ChevronRight size={24} fillColor='#111111' />
            </DescriptionButton>
          )}
          <DescriptionButton
            onClick={() => {
              if (isUploadRequired) {
                history.push(routes.locationCustoms);
              } else {
                history.push(routes.airportLocation);
              }
            }}
          >
            <Description>
              <img src={ICON_CUSTOM} alt='' width={24} />
              {text.kioskLocation[nationIndex]}
            </Description>
            <Icon.ChevronRight size={24} fillColor='#111111' />
          </DescriptionButton>
          {!isDeparture && (
            <DescriptionButton
              onClick={() => {
                localStorage.setItem(`${passportNumber}-transfer`, 'true');
                history.push(routes.airportNonRefundable);
              }}
            >
              <Description>
                <img src={ICON_REMITTANCE} alt='' width={24} />
                {text.getByRemittance[nationIndex]}
              </Description>
              <Icon.ChevronRight size={24} fillColor='#111111' />
            </DescriptionButton>
          )}
        </DescriptionWrapper>
        <BottomSection>
          <TextButton
            title={text.logOut[nationIndex]}
            onClick={onOpenLogoutModal}
            color='#1856CD'
            bgColor='#fff'
            outline
          />
        </BottomSection>
      </ReceiptLayout>
      {openedBillIndex > -1 && data?.items && (
        <DetailBillModal
          billList={data.items}
          openedId={openedBillIndex}
          onClose={() => setOpenedBillIndex(-1)}
        />
      )}
    </>
  );
};

const ButtonWrapper = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 24px;
  margin-bottom: 16px;
`;

const Title = styled.p`
  color: var(--KTP-Primary-100, var(--KTP-Color-Primary-100, #246cf6));
  font-size: 24px;
  font-weight: 700;
  line-height: 140%; /* 33.6px */
  letter-spacing: -0.48px;
  margin-bottom: 16px;
`;

const RefundSection = styled.div`
  width: 100%;
  margin-bottom: 16px;
`;

const DescriptionWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  gap: 12px;
  margin: 48px 0 28px;
`;

const DescriptionButton = styled.button`
  width: 100%;
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px;
  border-radius: 12px;
  box-shadow: 0px 0px 20px 0px rgba(174, 191, 204, 0.5);
`;

const Description = styled.span`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 12px;
  color: #111;
  font-size: 16px;
  font-weight: 600;
  line-height: 64px;
`;

const BottomSection = styled.div`
  position: relative;
  width: 100%;
  background-color: #ffffff;
  padding: 28px 0 36px 0;

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 50%;
    transform: translateX(-50%);
    width: calc(100% + 40px);
    height: 8px;
    background-color: #f5f6f7;
  }
`;

export default Airport;
